export type AdminToolsConfig = {
  /** When enabled, the map panels show lat/lon under the cursor. */
  showHoverCoordinates: boolean;
};

const STORAGE_KEY = 'adminToolsConfig';

const defaultConfig: AdminToolsConfig = {
  showHoverCoordinates: false,
};

const readFromStorage = (): AdminToolsConfig => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return defaultConfig;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultConfig;
    }
    const parsed = JSON.parse(raw) as Partial<AdminToolsConfig>;
    return {
      ...defaultConfig,
      showHoverCoordinates:
        typeof parsed.showHoverCoordinates === 'boolean'
          ? parsed.showHoverCoordinates
          : defaultConfig.showHoverCoordinates,
    };
  } catch (error) {
    console.warn('[adminToolsStore] failed to read config, using defaults', error);
    return defaultConfig;
  }
};

let state: AdminToolsConfig = readFromStorage();

type Listener = (config: AdminToolsConfig) => void;
const listeners = new Set<Listener>();

const persist = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[adminToolsStore] failed to persist config', error);
  }
};

const notify = () => {
  for (const listener of listeners) {
    listener({ ...state });
  }
};

export const getAdminToolsConfig = (): AdminToolsConfig => ({ ...state });

export const updateAdminToolsConfig = (updates: Partial<AdminToolsConfig>) => {
  state = {
    ...state,
    showHoverCoordinates:
      typeof updates.showHoverCoordinates === 'boolean'
        ? updates.showHoverCoordinates
        : state.showHoverCoordinates,
  };
  persist();
  notify();
};

export const subscribeAdminToolsConfig = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener({ ...state });
  return () => listeners.delete(listener);
};
